import React, { useState } from 'react';
import { Checkbox, Form, Input, InputNumber, Radio, Button, message } from 'antd';
import viatouchLogoBlack from './../../../assets/img/order-page/viatouch-logo-black.png';
import './Readiness-form.scss';

function ReadinessForm({ accessToken }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    const onFinish = async (formValue) => {
        setLoading(true);
        try {
            const response = await fetch('https://viatouchmedia-test.apigee.net/loyalty/microsite/readiness?to=viktor.kazmirchuk@oril.co', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'access_token': accessToken,
                },
                body: JSON.stringify(formValue),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Something went wrong!');
            }

            messageApi.success('Readiness form successfully submitted!');
            form.resetFields();
        } catch (err) {
            messageApi.error('Failed to submit Readiness form: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={'readiness-form-wrapper'}>
            {contextHolder}

            <div className={'readiness-header'}>
                <img src={viatouchLogoBlack} alt="ViaTouch Media" className={'readiness-logo'} />
                <p className={'readiness-description'}>
                    Use this checklist when performing site visits to ensure the necessary electrical,
                    internet, and space requirements are in place for the vending model below.
                </p>
                <p className={'readiness-subtitle'}>
                    PLANET FITNESS — VICKI by ViaTouch Media (Standard Planogram)
                </p>
            </div>

            <Form
                form={form}
                layout="horizontal"
                labelAlign="left"
                labelWrap
                colon={false}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 17 }}
                scrollToFirstError
                onFinish={onFinish}
                size="large"
            >
                {/* Machine Footprint */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Machine Footprint / Location Requirements</div>
                    <div className={'form-section-body'}>
                        <p className={'form-note-text'}>Footprint: 9 sq. ft.</p>
                        <div className={'measurements-table'}>
                            {[
                                { label: 'Width', value: '35.7 in. (90.7 cm)' },
                                { label: 'Depth**', value: '36.24 in. (92 cm)' },
                                { label: 'Height', value: '81.65 in. (207.4 cm)' },
                                { label: 'Weight', value: '850 lb. (386 kg)' },
                            ].map(row => (
                                <div key={row.label} className={'measurement-row'}>
                                    <div className={'measurement-label'}>{row.label}</div>
                                    <div className={'measurement-value'}>{row.value}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Site Access */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Site Access</div>
                    <div className={'form-section-body'}>
                        <div className={'access-columns'}>
                            <div className={'access-column'}>
                                <strong>On Pallet</strong>
                                <p>Door Opening Width: 42"</p>
                                <p>Door Opening Height: 86"</p>
                            </div>
                            <div className={'access-column'}>
                                <strong>Off Pallet</strong>
                                <p>Door Opening Width: 36"</p>
                                <p>Door Opening Height: 82"</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Machine Location */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Machine Location</div>
                    <div className={'form-section-body'}>
                        <div className={'access-columns'}>
                            <div className={'access-column'}>
                                <p>Indoor – protected</p>
                                <p>Clear of public thoroughfare</p>
                                <p>Clearance for door to open 90°</p>
                            </div>
                            <div className={'access-column'}>
                                <p>Clear of direct sunlight</p>
                                <p>Clear of emergency exits</p>
                                <p>Soft floor – requires support cups</p>
                            </div>
                        </div>
                        <Form.Item label="Other special requirements" name="otherSpecRequirements"
                            labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
                            <Input placeholder="Describe any additional requirements" />
                        </Form.Item>
                    </div>
                </div>

                {/* Location Access */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Location Access</div>
                    <div className={'form-section-body'}>
                        <div className={'checkbox-grid'}>
                            {[
                                { name: 'receivingDock', label: 'Receiving dock' },
                                { name: 'forkliftAvailable', label: 'Forklift available' },
                                { name: 'liftGateRequired', label: 'Lift gate required' },
                                { name: 'palletTruckAccess', label: 'Pallet truck access' },
                                { name: 'stairs', label: 'Stairs (need crawler)' },
                                { name: 'floorProtectionRequired', label: 'Floor protection required' },
                            ].map(item => (
                                <Form.Item key={item.name} name={item.name} valuePropName="checked" noStyle>
                                    <Checkbox>{item.label}</Checkbox>
                                </Form.Item>
                            ))}
                        </div>
                        <div className={'counter-fields'}>
                            <Form.Item label="# Hallways to location" name="hallwaysToLocation"
                                labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <InputNumber min={0} placeholder="0" style={{ width: '100px' }} />
                            </Form.Item>
                            <Form.Item label="# Single-entry doors" name="singleEntryDoorsToLocation"
                                labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <InputNumber min={0} placeholder="0" style={{ width: '100px' }} />
                            </Form.Item>
                            <Form.Item label="Single-entry door width(s)" name="singleEntryDoorWidth"
                                labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <InputNumber min={0} placeholder='inches' style={{ width: '100px' }} />
                            </Form.Item>
                        </div>
                    </div>
                </div>

                {/* Machine Requirements */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Machine Requirements</div>
                    <div className={'form-section-body'}>
                        <div className={'checkbox-stack'}>
                            <Form.Item name="airFlow" valuePropName="checked" noStyle>
                                <Checkbox>
                                    <strong>Air Flow:</strong> Ensure clear space of 4" (102mm) at the back and 2" (51mm) above the machine
                                </Checkbox>
                            </Form.Item>
                            <Form.Item name="electrical" valuePropName="checked" noStyle>
                                <Checkbox>
                                    <strong>Electrical:</strong> Dedicated 120V, 60Hz, 15/20AMP circuit/wall socket within 5ft of machine location
                                </Checkbox>
                            </Form.Item>
                        </div>
                    </div>
                </div>

                {/* Ethernet */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Ethernet Required</div>
                    <div className={'form-section-body'}>
                        <Form.Item name="ethernetRequired"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Radio.Group>
                                <Radio value="true">Yes</Radio>
                                <Radio value="false">No — default connectivity is cellular</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </div>
                </div>

                {/* Graphics */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Graphics</div>
                    <div className={'form-section-body'}>
                        <Form.Item
                            label='Quantity of "Stand Alone" Units'
                            name="standAloneUnits"
                            extra="*Recessed units are preferred by PFHQ where option is available"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <InputNumber min={0} placeholder="0" style={{ width: '120px' }} />
                        </Form.Item>
                    </div>
                </div>

                {/* Location Details */}
                <div className={'form-section'}>
                    <div className={'form-section-title'}>Location Details</div>
                    <div className={'form-section-body'}>
                        <Form.Item label="Location Name" name="locationName"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Club / location name" />
                        </Form.Item>
                        <Form.Item label="Quantity of VICKIs" name="quantityOfVickis"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <InputNumber min={1} placeholder="0" style={{ width: '120px' }} />
                        </Form.Item>
                        <Form.Item label="Site Contact Name" name="siteContactName"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Full name" />
                        </Form.Item>
                        <Form.Item label="Phone" name="phone"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Phone number" style={{ maxWidth: '260px' }} />
                        </Form.Item>
                        <Form.Item label="Location Address" name="address"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Street address" />
                        </Form.Item>
                        <Form.Item label="City" name="city"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="City" />
                        </Form.Item>
                        <Form.Item label="State / Province" name="state"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="State" style={{ maxWidth: '200px' }} />
                        </Form.Item>
                        <Form.Item label="Zip Code" name="zip"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <Input placeholder="Zip code" style={{ maxWidth: '160px' }} />
                        </Form.Item>
                        <Form.Item
                            label="Sales Tax % (products sold in VICKI)"
                            name="salesTax"
                            extra="*Completing this allows us to preload the Sales Tax rate to machines"
                            rules={[{ required: true, message: 'Field is required' }]}>
                            <InputNumber min={0} max={100} placeholder="%" style={{ width: '120px' }} />
                        </Form.Item>
                    </div>
                </div>

                <div className={'form-submit'}>
                    <Button type="primary" htmlType="submit" loading={loading} size="large">
                        Submit Readiness Form
                    </Button>
                </div>
            </Form>
        </div>
    );
}

export default ReadinessForm;
